/** RN002 — resultado do evento registrado na Auditoria; critério do filtro Status. */
export type AuditResult = "sucesso" | "falha";

/** Opção de um select de filtro: o rótulo na tela e o valor enviado na consulta. */
export interface AuditFilterOption {
  value: string;
  label: string;
}

/**
 * Alterações auditadas hoje pelo Hub. O valor é o código combinado com o
 * serviço e é o que segue na consulta; o rótulo é o texto da tela.
 */
export const AUDIT_ACTIONS = [
  "LOGIN",
  "USUARIO_CRIADO",
  "USUARIO_ATUALIZADO",
  "USUARIO_PERFIL_ATUALIZADO",
  "USUARIO_STATUS_ATUALIZADO",
  "PERFIL_CRIADO",
  "PERFIL_ATUALIZADO",
  "PERFIL_EXCLUIDO",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_ACTION_OPTIONS: AuditFilterOption[] = AUDIT_ACTIONS.map((value) => ({
  value,
  label: value,
}));

/** Módulos do Hub cobertos pela Auditoria, no mesmo contrato de código/rótulo. */
export const AUDIT_MODULES = ["OBRAS", "AUTENTICACAO", "USUARIOS", "PERFIS", "PERMISSOES"] as const;

export type AuditModule = (typeof AUDIT_MODULES)[number];

export const AUDIT_MODULE_LABELS: Record<AuditModule, string> = {
  OBRAS: "Obras",
  AUTENTICACAO: "Autenticação",
  USUARIOS: "Usuários",
  PERFIS: "Perfis",
  PERMISSOES: "Permissões",
};

export const AUDIT_MODULE_OPTIONS: AuditFilterOption[] = AUDIT_MODULES.map((value) => ({
  value,
  label: AUDIT_MODULE_LABELS[value],
}));

export function formatAuditModule(modulo: string): string {
  const key = modulo.trim().toUpperCase();
  return (AUDIT_MODULE_LABELS as Record<string, string>)[key] ?? modulo;
}

export interface AuditRecord {
  id: string;
  /** Data/hora do evento em ISO-8601; texto original quando o serviço não devolve ISO. */
  dataHora: string;
  acao: string;
  /** Valor antes da alteração; vazio nas ações que não trocam um valor (ex.: login). */
  valorAnterior: string;
  /** Valor depois da alteração; acompanha `valorAnterior` na coluna Ação. */
  valorNovo: string;
  justificativa: string;
  modulo: string;
  responsavel: string;
  /**
   * Cargo, perfil ou área do responsável: aparece abaixo do nome na listagem e
   * como o cargo do usuário no detalhamento. Vazio quando o serviço não devolve.
   */
  responsavelDetalhe: string;
  /** E-mail do responsável, exibido no detalhamento; vazio quando não há. */
  responsavelEmail: string;
  /** Telefone do responsável, exibido no detalhamento; vazio quando não há. */
  responsavelTelefone: string;
}

/** Critérios da área de filtros; combinados entre si (RN003). */
export interface AuditFilters {
  status: AuditResult | null;
  /** Ano do evento; a consulta recebe o período do 1º ao último dia do ano. */
  ano: number | null;
  /** Código da alteração auditada; "" quando o critério não está aplicado. */
  alteracao: AuditAction | "";
  /** Código do módulo auditado; "" quando o critério não está aplicado. */
  modulo: AuditModule | "";
  /** Identificador do usuário escolhido no select de Responsável. */
  responsavelId: string;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  status: null,
  ano: null,
  alteracao: "",
  modulo: "",
  responsavelId: "",
};

export type AuditSortOrder = "asc" | "desc";

export interface ListAuditRecordsParams extends Partial<AuditFilters> {
  pagina?: number;
  tamanhoPagina?: number;
  /** Ordenação da listagem: `desc` = mais recente primeiro (padrão). */
  ordem?: AuditSortOrder;
  signal?: AbortSignal;
}

export interface AuditRecordsPage {
  records: AuditRecord[];
  total: number;
}

export type AuditStatus = "loading" | "success" | "error" | "forbidden";

export interface AuditQueryState {
  status: AuditStatus;
  records: AuditRecord[];
  total: number;
  errorMessage: string | null;
}

/** Consulta somente leitura do registro acionado em Detalhar (RN001). */
export interface GetAuditRecordParams {
  id: string;
  signal?: AbortSignal;
}

/**
 * Estados do detalhamento. Cada cenário de falha tem tratamento próprio:
 * `sessionExpired` (401), `forbidden` (403), `notFound` (404) e `error` para a
 * falha de comunicação.
 */
export type AuditDetailStatus =
  "loading" | "success" | "sessionExpired" | "forbidden" | "notFound" | "error";

export interface AuditDetailState {
  status: AuditDetailStatus;
  /** Preenchido apenas em `success`: nenhuma falha exibe dados do registro. */
  record: AuditRecord | null;
  errorMessage: string | null;
}
